import axios, { AUTH_HEADER_KEY } from "../utils/minimal/axios";
import { reportEmptyParentToSentry } from "../utils/issue302Reporter.js";

/* ------------------ User ------------------ */
export const logIn = async ({ email, password }) => {
    return axios.post('/user/login', { email, password })
}

export const verify2FA = async ({ userId, code }) => {
    return axios.post('/user/verify-2fa', { userId, code })
}

export const resend2FA = async ({ userId }) => {
    return axios.post('/user/resend-2fa', { userId })
}

export const verifyToken = async (token) => {
    return axios.get('/user/verifyToken', { headers: { [AUTH_HEADER_KEY]: token } })
}

export const updateSeasonTicketsDiscount = async ({ accountId, eventId, data }) => {
    return axios.put(`/event/updateEventSeasonTicketDiscount/${accountId}/${eventId}`, data)
}

export const getAccountsByJwt = async (token) => {
    const callId = `${Date.now()}-${Math.random()}`;  // Unique call ID
    const callTimestamp = Date.now();

    const response = await axios.get('/user/account/getByJWT', { headers: { [AUTH_HEADER_KEY]: token } })

    // 🔍 Check for empty parent objects immediately
    const accountsWithEmptyParent = response.data.filter(account =>
        account.isSubAccount &&
        account.parent &&
        typeof account.parent === 'object' &&
        Object.keys(account.parent).length === 0
    );

    // 🔍 Issue #302: Report empty parent objects to Sentry (TEMPORARY - will be removed)
    if (accountsWithEmptyParent.length > 0) {
        reportEmptyParentToSentry({
            accountsWithEmptyParent,
            allAccounts: response.data,
            callId,
            callTimestamp,
            responseHeaders: response.headers
        });
    }

    return response;
}

export const getUser = async (userId) => {
    return axios.get(`/user/${userId}`)
}

export const getAccountCollaborates = (accountId) => {
    return axios.get(`/user/account/getCollaboratorsAndInvitees/${accountId}`)
}

export const register = async (userData) => {
    return axios.post('/user/register', userData)
}

export const updateUser = async (userData) => {
    return axios.put('/user/update', userData)
}

export const forgotPassword = async (forgotPasswordData) => {
    return axios.post('/user/forgotPassword', forgotPasswordData)
}

export const updatePassword = async (data) => {
    return axios.put('/user/updatePassword', data)
}

// Public (no jwt): the single-use token from the emailed link is the credential. An invalid,
// used or expired link is an ordinary outcome the page renders, so don't log it as a failure.
export const verifyEmail = async ({ token }) => {
    return axios.post('/user/verify-email', { token }, { skipErrorLog: true })
}

export const resendVerificationEmail = async () => {
    return axios.post('/user/resend-verification-email')
}

export const logOut = async () => {
    const data = await axios.put('/user/logout')
}

export const getAccount = async (accountId, token) => {
    return axios.get(`/user/account/${accountId}`, { headers: { [AUTH_HEADER_KEY]: token } })
}

export const getAccountSecured = async (accountId) => {
    return axios.get(`/user/account/${accountId}`)
}

export const getAccountIdByHandler = async (handler) => {
    // Check for mock data first
    const { getMockAccount } = await import('../services/mockService');
    const mockResponse = await getMockAccount(handler);
    if (mockResponse) return mockResponse;

    return axios.get(`/user/account/getByHandler/${handler}`)
}

export const createSubAccount = async ({ accountId, subAccountData }) => {
    return axios.post(`/user/account/createSubAccount/${accountId}`, subAccountData)
}

export const updateAccount = async ({ accountId, subAccountData }) => {
    return axios.put(`/user/account/updateAccount/${accountId}`, subAccountData)
}

export const getSubAccounts = async (accountId, token) => {
    return axios.get(`/user/account/subAccounts/${accountId}`, { headers: { [AUTH_HEADER_KEY]: token } })
}

export const inviteCollaborator = async ({ accountId, collaborateData }) => {
    return axios.post(`/user/account/inviteCollaborator/${accountId}`, collaborateData)
}

export const inviteSellerCollaborator = async ({ accountId, collaborateData }) => {
    return axios.post(`/user/account/inviteSellerCollaborator/${accountId}`, collaborateData)
}

export const getCrmEventTypes = async ({ marketplaceId, sellerId }) => {
    return axios.get(`/crm/getCrmEventTypes/${marketplaceId}/${sellerId}`)
}

export const acceptInvitation = async (inviteeToken, token) => {
    return axios.post("/user/account/acceptInvitation", { token: inviteeToken }, { headers: { [AUTH_HEADER_KEY]: token } })
}

export const updateCollaborate = async ({ accountId, userId, role }) => {
    return axios.post(`/user/account/updateCollaborator/${accountId}`, { userId, role })
}

export const deleteCollaborate = async ({ accountId, userId }) => {
    return axios.post(`/user/account/deleteCollaborator/${accountId}`, { userId })
}

export const deleteInvitee = async ({ accountId, email }) => {
    return axios.post(`/user/account/deleteInvitee/${accountId}`, { email })
}

export const resendInvitation = async ({ accountId, email }) => {
    return axios.post(`/user/account/resendInvitation/${accountId}`, { email })
}

// With a marketplaceId, only that marketplace and only if the user owns it (an owner of two
// marketplaces otherwise gets an arbitrary one — #1397).
export const getMarketplaceOfUser = (token, marketplaceId) => {
    return axios.get("/user/marketplace", {
        headers: { [AUTH_HEADER_KEY]: token },
        params: marketplaceId ? { marketplaceId } : undefined,
    })
}

export const getMarketplaceById = (marketplaceId) => {
    return axios.get(`/user/marketplace/${marketplaceId}`)
}

export const getMarketplaceIdByDomainRegex = (domain) => {
    return axios.get(`/user/marketplace/getMarketplaceIdByDomainRegex?domain=${domain}`)
}

export const getAccountsOfMarketplace = (marketPlaceId) => {
    return axios.get(`/user/marketplace/accounts/${marketPlaceId}`)
}

// Sellers overview (My Sellers feature strip): SMS credits, AI usage, events, last activity per
// seller + sub-account. Marketplace-owner guarded, same as the accounts route.
export const getMarketplaceSellersOverview = (marketplaceId) => {
    return axios.get(`/user/marketplace/${marketplaceId}/sellers-overview`)
}

// Grants SMS credits to a top-level seller of the marketplace (logged on the BE).
// Resolves with { sellerId, smsCredits } — the NEW balance. `note` is omitted when empty.
export const grantSellerSmsCredits = ({ marketplaceId, sellerId, amount, note }) => {
    return axios.post(`/user/marketplace/${marketplaceId}/sellers/${sellerId}/sms-credits`, note ? { amount, note } : { amount })
}

export const createAccountUnderMarketplace = (marketPlaceId, accountData) => {
    return axios.post(`/user/account/createAccount/${marketPlaceId}`, accountData)
}

export const updateAccountWithMarketplaceFinancial = (marketPlaceId, accountId) => {
    return axios.put(`/user/account/updateAccountWithMarketplaceFinancial/${marketPlaceId}/${accountId}`)
}

export const createNewSellerPartner = (accountSellerData) => {
    return axios.post(`/user/seller-partner/create`, accountSellerData)
}

export const createSellerPublic = (marketplaceId, sellerData) => {
    // get utm from url and pass it to the api the same way as the url
    const utm = window.location.search.split('utm=')[1]
    return axios.post(`/user/account/createSellerPublic/${marketplaceId}`, sellerData, { params: { utm } })
}

export const getEndUserById = ({ marketplaceId, accountId, endUserId }) => {
    return axios.get(`/user/enduser/getById/${marketplaceId}/${accountId}/${endUserId}`)
}

export const getEndUserByIdPublic = ({ marketplaceId, accountId, endUserId }) => {
    return axios.get(`/user/enduser/publicGetById/${marketplaceId}/${accountId}/${endUserId}`)
}

export const getEndUserCRMEvents = ({ marketplaceId, sellerId, endUserId }) => {
    return axios.get(`/crm/getEndUsersCRMEventByEndUser/${marketplaceId}/${sellerId}/${endUserId}`)
}

// By 'account' we mean the parent account (seller) and not sub account
export const getEndUsersOfAccount = ({ marketplaceId, accountId, page = 1, limit = 500 }) => {
    return axios.get(`/user/enduser/getByAccount/${marketplaceId}/${accountId}?page=${page}&limit=${limit}`)
}

export const aggregateEndUsersBySellerByMonthOrSpecificDates = ({ marketplaceId, sellerId, startDate, endDate }) => {
    return axios.get(`/user/enduser/aggregateEndUsersBySellerByMonthOrSpecificDates/${marketplaceId}/${sellerId}?startDate=${startDate}&endDate=${endDate}`)
}

export const getSegmentsOfAccount = ({ marketplaceId, accountId }) => {
    return axios.get(`/user/segment/getByAccount/${marketplaceId}/${accountId}`)
}

export const updateEventBehaviorWithSegments = ({ marketplaceId, accountId, eventId, data }) => {
    return axios.put(`/event/updateEventBehaviorWithSegments/${marketplaceId}/${accountId}/${eventId}`, data)
}

export const getEndUserSegmentsById = ({ marketplaceId, accountId, endUserId }, options) => {
    return axios.get(`/user/enduser/getSegmentsById/${marketplaceId}/${accountId}/${endUserId}`, options)
}

export const createSegment = ({ marketplaceId, accountId, segmentData }) => {
    return axios.post(`/user/segment/create/${marketplaceId}/${accountId}`, segmentData)
}

export const editSegment = ({ marketplaceId, accountId, segmentId, segmentData }) => {
    return axios.put(`/user/segment/update/${marketplaceId}/${accountId}/${segmentId}`, segmentData)
}

export const getEndUsersOfSegment = ({ marketplaceId, accountId, segmentId, shouldPopulateEndUser = false }) => {
    let url = `/user/segment/getEndUsersBySegmentId/${marketplaceId}/${accountId}/${segmentId}`
    if (shouldPopulateEndUser) {
        url += '?shouldPopulateEndUser=true'
    }
    return axios.get(url)
}

export const assignEndUsersToSegment = ({ marketplaceId, accountId, segmentId, endUsersIds }) => {
    return axios.post(`/user/segment/addEndUsers/${marketplaceId}/${accountId}/${segmentId}`, { endUsers: endUsersIds })
}

export const syncSegmentWithEndUsers = ({ marketplaceId, accountId, segmentId }) => {
    return axios.put(`/user/segment/syncSegmentWithEndUsers/${marketplaceId}/${accountId}/${segmentId}`)
}

export const removeEndUsersFromSegment = ({ marketplaceId, accountId, segmentId, endUsersIds }) => {
    return axios.post(`/user/segment/removeEndUsers/${marketplaceId}/${accountId}/${segmentId}`, { endUsers: endUsersIds })
}

export const getAccountByIdWithToken = (accountId, token) => {
    return axios.get(`/user/account/${accountId}`, { headers: { [AUTH_HEADER_KEY]: token } })
}

export const getAccountById = (accountId) => {
    return axios.get(`/user/account/${accountId}`)
}

export const updateEndUser = ({ marketplaceId, accountId, endUserId, endUserData }) => {
    return axios.put(`/user/enduser/update/${marketplaceId}/${accountId}/${endUserId}`, endUserData)
}

export const createEndUser = ({ marketplaceId, accountId, endUserData }) => {
    return axios.post(`/user/enduser/create/${marketplaceId}/${accountId}`, endUserData)
}

// Soft-deletes an end user (backend sets isDelete=true on the end user and its season tickets).
// Refuses (409) if the end user has an active season ticket; requires account admin role.
export const deleteEndUser = ({ marketplaceId, accountId, endUserId }) => {
    return axios.delete(`/user/enduser/delete/${marketplaceId}/${accountId}/${endUserId}`)
}

export const generateLead = ({ marketplaceId, accountId, endUserData }) => {
    return axios.post(`/user/enduser/generate-lead/${marketplaceId}/${accountId}`, endUserData)
}

export const searchEndUsersByTerm = ({ marketplaceId, accountId, term, page = 1, limit = 500, segments, segmentsToExclude, fromDate, toDate, minPrice, maxPrice }) => {
    let url = `/user/enduser/searchByTerm/${marketplaceId}?limit=${limit}&page=${page}&accountId=${accountId}&term=${encodeURIComponent(term ?? '')}`
    // convert segments to string
    if (segments && segments.length) {
        segments.forEach((segment) => {
            url += `&segments=${segment}`
        })
    }
    if (segmentsToExclude && segmentsToExclude.length) {
        segmentsToExclude.forEach((segment) => {
            url += `&segmentsToExclude=${segment}`
        })
    }
    if (fromDate) {
        url += `&fromDate=${fromDate}`
    }
    if (toDate) {
        url += `&toDate=${toDate}`
    }
    if (minPrice) {
        url += `&minPrice=${minPrice}`
    }
    if (maxPrice) {
        url += `&maxPrice=${maxPrice}`
    }
    return axios.get(url)
}

export const getSubAccountsByParentId = (accountId) => {
    return axios.get(`/user/account/subAccountsByParentId/${accountId}`)
}


export const getEventById = async ({ accountId, eventId }) => {
    return axios.get(`/event/getById/${accountId}/${eventId}`)
}

export const getAccountEvents = async (accountId, options) => {
    return axios.get(`/event/getEventsOfAccount/${accountId}`, options)
}

export const getMarketPlaceEvents = async (marketplaceId) => {
    return axios.get(`/event/getEventsByMarketplace/${marketplaceId}`)
}

export const getSellerEvents = async (sellerId) => {
    return axios.get(`/event/getEventsBySeller/${sellerId}`)
}

export const getSellerEventsPaginated = async ({ sellerId, limit, skip }) => {
    return axios.get(`/event/getEventsBySellerPaginated/${sellerId}`, { params: { limit, skip } })
}

/**
 * Search events by name for a specific seller (searches across all sub-accounts)
 * @param {Object} params - Search parameters
 * @param {string} params.sellerId - Primary seller account ID
 * @param {string} params.name - Search term (case-insensitive partial match)
 * @param {number} params.limit - Max results (default: 10)
 * @param {number} params.skip - Pagination offset (default: 0)
 * @returns {Promise} API response with filtered events
 */
export const searchEventsBySellerByName = async ({ sellerId, name, limit = 50, page = 1 }) => {
    return axios.get(`/event/getEventsBySellerByName/${sellerId}`, {
        params: { name, limit, page }
    })
}

export const searchEventsByMarketplaceByName = async ({ marketplaceId, name, limit = 10, page = 1 }) => {
    return axios.get(`/event/getEventsByMarketplaceByName/${marketplaceId}`, {
        params: { name, limit, page }
    })
}

export const getByOrderNumberForMarketplace = async ({ marketplaceId, orderNumber }) => {
    return axios.get(`/user-event/event-order/getByOrderNumberForMarketplace/${marketplaceId}/${orderNumber}`)
}

// #1306 — dateMode 'orderDate' scopes the range to order dates (ongoing events); omitted = event date.
export const getMarketplaceSaleStats = ({ marketplaceId, from, to, dateMode }) => {
    const params = { from, to, ...(dateMode === 'orderDate' && { dateMode }) }
    return axios.get(`/user-event/marketplace/sale-stats/${marketplaceId}`, { params })
}

// #1054 — on-demand Payme fee refresh for one event ("reconcile now" on the sale-stats table).
// Synchronous: resolves with the merchant sweep summary; caller refetches sale-stats after.
export const reconcileMarketplaceEventFees = ({ marketplaceId, eventId }) => {
    return axios.post(`/user-event/marketplace/sale-stats/${marketplaceId}/reconcile-event/${eventId}`)
}

// #1057 — seller advance payments ledger (event service). Owner-only writes; the list is also
// readable by the event's seller account.
export const createAdvancePayment = ({ marketplaceId, eventId, advanceData }) => {
    return axios.post(`/event/marketplace/${marketplaceId}/event/${eventId}/advance-payment`, advanceData)
}

export const getAdvancePayments = ({ marketplaceId, eventId }) => {
    return axios.get(`/event/marketplace/${marketplaceId}/event/${eventId}/advance-payments`)
}

export const deleteAdvancePayment = ({ marketplaceId, advancePaymentId }) => {
    return axios.delete(`/event/marketplace/${marketplaceId}/advance-payment/${advancePaymentId}`)
}

export const getByOrderNumberForSeller = async ({ accountId, marketplaceId, orderNumber }) => {
    return axios.get(`/user-event/event-order/getByOrderNumberForSeller/${accountId}/${marketplaceId}/${orderNumber}`)
}

export const getEvent = async ({ accountId, eventId }) => {
    return axios.get(`/event/getById/${accountId}/${eventId}`)
}

export const createEvent = async ({ accountId, eventData }) => {
    return axios.post(`/event/createEvent/${accountId}`, eventData)
}

export const updateEvent = async ({ accountId, eventId, eventData }) => {
    return axios.put(`/event/updateEvent/${accountId}/${eventId}`, eventData)
}

export const deleteEvent = async ({ accountId, eventId }) => {
    const data = await axios.delete(`/event/deleteEvent/${accountId}/${eventId}`)
}

export const updateEventTicket = async ({ accountId, eventId, ticketId, ticketData }) => {
    return axios.put(`/event/ticket/update/${accountId}/${eventId}/${ticketId}`, ticketData)
}

export const fetchEventTickets = async ({ accountId, eventId, nimiFeeType }) => {
    // Check for mock data first
    const { getMockTickets } = await import('../services/mockService');
    const mockResponse = await getMockTickets(accountId, eventId);
    if (mockResponse) return mockResponse;

    let url = `/event/ticket/getTickets/${accountId}/${eventId}`
    if (nimiFeeType) {
        url += `?nimiFeeType=${nimiFeeType}`
    }
    return axios.get(url)
}

// This is will give the full ticket's data
export const fetchEventTicketsSecure = async ({ accountId, eventId }) => {
    return axios.get(`/event/ticket/getTicketsSecured/${accountId}/${eventId}`)
}

export const createEventTicket = async ({ accountId, eventId, ticketData }) => {
    return axios.post(`/event/ticket/create/${accountId}/${eventId}`, ticketData)
}

export const getEventByHandler = async ({ accountId, eventHandler, utm }) => {
    // Check for mock data first
    const { getMockEvent } = await import('../services/mockService');
    const mockResponse = await getMockEvent(accountId, eventHandler);
    if (mockResponse) return mockResponse;

    let url = `/event/getEventByHandler/${accountId}/${eventHandler}`
    if (utm) {
        url += `?utm=${utm}`
    }
    return axios.get(url)
}
// get event by id
export const getEventJustById = async ({ eventId }) => {
    return axios.get(`/event/getPublicEventById/${eventId}`)
}

export const checkIn = async ({ accountId, eventId, userEventId, ticketInfoId }) => {
    return axios.put(`/user-event/web-qr-check-in/${accountId}/${eventId}/${userEventId}/${ticketInfoId}`)
}

// payment/private-club-check-in/:sellerId/:endUserSeasonTicketId
export const subscriptionCheckIn = async ({ sellerId, subscriptionId }) => {
    return axios.put(`/payment/private-club-check-in/${sellerId}/${subscriptionId}`)
}

export const getTicketById = async ({ accountId, eventId, ticketId }) => {
    return axios.get(`/event/ticket/getById/${accountId}/${eventId}/${ticketId}`)
}

// Should not be in use any more after we changes to eventLinks entity instead of having it on the event itself as an array.
export const updateOrCreateEventLinks = async ({ accountId, eventId, linksArray }) => {
    return axios.put(`/event/updateOrCreateEventLinks/${accountId}/${eventId}`, linksArray)
}

export const applySeatsIoChartToEvent = async ({ accountId, eventId, seatsChartKey }) => {
    return axios.post(`/event/createSeatsEventAndUpdateEvent/${accountId}/${eventId}/${seatsChartKey}`)
}

export const deleteTicket = async ({ accountId, eventId, ticketId }) => {
    return axios.delete(`/event/ticket/delete/${accountId}/${eventId}/${ticketId}`)
}

export const getLinksOfEventsSecured = async ({ accountId, eventId }) => {
    return axios.get(`/event/event-link/getEventLinksSecured/${accountId}/${eventId}`)
}

export const getLinksOfEvents = async ({ accountId, eventId }) => {
    return axios.get(`/event/event-link/getEventLinks/${accountId}/${eventId}`)
}

export const getLinksOfEventsByUtm = async ({ accountId, eventId, utm }) => {
    return axios.get(`/event/event-link/getEventLinksOfEventByUTMUnsecured/${accountId}/${eventId}/${utm}`)
}

export const getLinksByCollaborator = async (collaboratorId) => {
    return axios.get(`/event/event-link/getByCollaborator/${collaboratorId}`)
}

export const createLinkOfEvent = async ({ accountId, eventId, linkData }) => {
    return axios.post(`/event/event-link/create/${accountId}/${eventId}`, linkData)
}

export const createManyLinksOfEvent = async ({ accountId, eventId, linksDataArr }) => {
    return axios.post(`/event/event-link/createMany/${accountId}/${eventId}`, linksDataArr)
}

export const updateLinkOfEvent = async ({ accountId, eventId, linkId, linkData }) => {
    return axios.put(`/event/event-link/update/${accountId}/${eventId}/${linkId}`, linkData)
}

export const deleteLinkOfEvent = async ({ accountId, eventId, linkId }) => {
    return axios.delete(`/event/event-link/delete/${accountId}/${eventId}/${linkId}`)
}

export const getPublicEventsOfAccount = (accountId) => {
    return axios.get(`/event/getPublicEventsByAccountId/${accountId}`)
}

export const getPublicSubscriptionsOfAccount = (accountId) => {
    return axios.get(`/event/getPublicSubscriptionsByAccountId/${accountId}`)
}

/* ------------------ User Event ------------------ */
export const getUserEventsByEndUser = ({ accountId, endUserId }) => {
    return axios.get(`/user-event/getByEndUser/${accountId}/${endUserId}`)
}

export const createUserEvent = async ({ accountId, eventId, ticketId, userEvent }) => {
    return axios.post(`/user-event/create/${accountId}/${eventId}/${ticketId}`, userEvent)
}

export const createEventOrder = async ({ accountId, eventId, eventOrder }) => {
    return axios.post(`/user-event/event-order/create/${accountId}/${eventId}`, eventOrder)
}

export const getAccountUserEvent = async ({ accountId }) => {
    const data = await axios.get(`/user-event/getUserEventsOfAccount/${accountId}`)
}

export const updateUserEvent = async ({ accountId, eventId, userEventId, userEventData }) => {
    return axios.put(`/user-event/updateUserEvent/${accountId}/${eventId}/${userEventId}`, userEventData)
}

export const getUserEventById = async ({ accountId, eventId, userEventId }) => {
    return axios.get(`/user-event/getById/${accountId}/${eventId}/${userEventId}`)
}

export const getUserEventsOfEvent = async ({ accountId, eventId }) => {
    return axios.get(`/user-event/getUserEventsOfEvent/${accountId}/${eventId}`)
}

export const getEventOrdersOfEvent = async ({ accountId, eventId }) => {
    return axios.get(`/user-event/event-order/getEventOrdersOfEvent/${accountId}/${eventId}`)
}

export const getEventOrderStats = async ({ accountId, eventId, from, to } = {}) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    return axios.get(
        `/user-event/event-order/getEventOrderStats/${accountId}/${eventId}`,
        { params }
    ).then(res => res.data);
}

export const getTimeSlotBookingsByDateAndSlot = async ({ accountId, eventId, from, to } = {}) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    return axios.get(
        `/user-event/event-order/getTimeSlotBookingsByDateAndSlot/${accountId}/${eventId}`,
        { params }
    ).then(res => res.data);
}

// Batched time-slot booking stats for multiple events in one call (per account).
// Collapses the per-event getTimeSlotBookingsByDateAndSlot fan-out. Returns
// { [eventId]: { [date]: { daySummary, bySlot } } } — same per-event/per-date shape
// as the single endpoint; events/dates with no bookings are omitted.
export const getTimeSlotBookingsForEvents = async ({ accountId, eventIds, from, to } = {}) => {
    return axios.post(
        `/user-event/event-order/getTimeSlotBookingsForEvents/${accountId}`,
        { eventIds, from, to }
    ).then(res => res.data);
}

export const getEventOrderToDisplayTickets = (eventOrderId) => {
    return axios.get(`/user-event/event-order/getEventOrderToDisplayTickets/${eventOrderId}`)
}

// This is legacy
export const checkInUser = ({ accountId, eventId, userEventId }) => {
    return axios.put(`/user-event/check-in/${accountId}/${eventId}/${userEventId}`)
}
// This is new (per ticket info)
export const checkInUserQr = ({ accountId, eventId, userEventId, ticketInfoId }) => {
    return axios.put(`/user-event/qr-check-in/${accountId}/${eventId}/${userEventId}/${ticketInfoId}`)
}

export const resendEmail = ({ accountId, eventId, eventOrderId }) => {
    return axios.post(`/user-event/event-order/reSendEmail/${accountId}/${eventId}/${eventOrderId}`)
}

export const updateEventOrder = ({ accountId, eventId, eventOrderId, orderEventData }) => {
    return axios.put(`/user-event/event-order/updateEventOrder/${accountId}/${eventId}/${eventOrderId}`, orderEventData)
}

export const getUserEventAppleWalletPass = (userEventId, ticketInfoId, event = null) =>
    axios.post(
        `/user-event/wallet/apple-pass/${userEventId}/${ticketInfoId}`,
        { event },
        { responseType: 'blob' }
    )

export const getUserEventGoogleWalletUrl = (userEventId, ticketInfoId, event = null) =>
    axios.post(
        `/user-event/wallet/google-pass-url/${userEventId}/${ticketInfoId}`,
        { event }
    )

export const getSubscriptionAppleWalletPass = (endUserSeasonTicketId, token, language = 'he') =>
    axios.get(
        `/payment/wallet/apple-pass/${endUserSeasonTicketId}`,
        { params: { language }, responseType: 'blob', headers: { [AUTH_HEADER_KEY]: token } }
    )

export const getSubscriptionGoogleWalletUrl = (endUserSeasonTicketId, token, language = 'he') =>
    axios.get(
        `/payment/wallet/google-pass-url/${endUserSeasonTicketId}`,
        { params: { language }, headers: { [AUTH_HEADER_KEY]: token } }
    )

export const getEventOrdersOfByUserEvents = ({ marketplaceId, accountId, userEventsIds }) => {
    return axios.post(`/user-event/event-order/getByUserEventsIds/${marketplaceId}/${accountId}`, { userEventsIds })
}

export const getTotalOrdersEarnedBySellerGroupsByEvent = ({ marketplaceId, sellerId }) => {
    return axios.get(`/user-event/event-order/aggregateTotalEarnedBySellerGroupedByEvent/${marketplaceId}/${sellerId}`)
}

export const getTotalOrdersEarnedBySeller = ({ marketplaceId, sellerId, startDate, endDate, byMarketplace }) => {
    let url = `/user-event/event-order/aggregateTotalEarnedBySeller/${marketplaceId}/${sellerId}`
    if (startDate && endDate) {
        url += `?startDate=${startDate}&endDate=${endDate}`
    }
    if (byMarketplace) {
        url += url.includes('?') ? "&byMarketplace=true" : `?byMarketplace=true`
    }
    return axios.get(url)
}

export const getEventOrdersOfAccount = ({ accountId, isSeller = true, limit = 10 }) => {
    let url = `/user-event/event-order/getEventOrdersOfAccount/${accountId}?limit=${limit}`
    if (isSeller) {
        url += `&isSeller=${isSeller}`
    }
    return axios.get(url)
}

/* ------------------ Payment ------------------ */
export const generateSale = async ({ accountId, eventId, paymentToken, token }) => {
    return axios.post(`/payment/generate-sale/${accountId}/${eventId}`, {}, { headers: { 'authorization-payment': paymentToken, [AUTH_HEADER_KEY]: token } })
}

export const getEventCoupons = async ({ accountId, eventId }) => {
    return axios.get(`/payment/coupon/getByEvent/${accountId}/${eventId}`)
}

export const getCouponByCode = async ({ accountId, eventId, code }) => {
    return axios.get(`/payment/coupon/getByCode/${accountId}/${eventId}?code=${code}`)
}

export const getCouponsByAccount = async ({ accountId }) => {
    return axios.get(`/payment/coupon/getByAccount/${accountId}`)
}

// The buyer's club grid. Public, non-secret rows ({_id, displayName, logoUrl, providerType, order})
// from the voucher-source collection — NOT embedded on the account (credentials never leave the
// payment service). See voucher-redemption backend design §3.1.
export const getVoucherSourcesPublic = async ({ accountId }) => {
    return axios.get(`/user/voucher-sources/public/${accountId}`)
}

// --- Admin: manage benefit clubs (voucher sources) ---
// There is deliberately no `getVoucherProviders` here. `GET /user/voucher-providers` does not exist
// on the server and never has — the backend design (voucher-redemption §3.1, "Three read shapes")
// specifies only the three voucher-sources routes below. Because nothing matched it, the request
// fell through to the `/user/:id` wildcard, which read "voucher-providers" as a user id and returned
// a 500 CastError on every load of the Benefit Clubs settings page. The admin credential form
// renders from KNOWN_PROVIDER_CREDENTIALS instead; adding provider #N updates that map.

// Admin list for a seller account — credentials returned MASKED (last-8). Editor role.
export const getVoucherSourcesAdmin = async ({ accountId }) => {
    return axios.get(`/user/voucher-sources/${accountId}`)
}

export const createVoucherSource = async ({ accountId, data }) => {
    return axios.post(`/user/voucher-source/${accountId}`, data)
}

// Write-only-secret guard on the backend: a masked/empty credential can't overwrite the stored key.
// Route is scoped by accountId + id (matches feat/voucher-2: /user/voucher-source/:accountId/:id).
export const updateVoucherSource = async ({ accountId, voucherSourceId, data }) => {
    return axios.put(`/user/voucher-source/${accountId}/${voucherSourceId}`, data)
}

export const deleteVoucherSource = async ({ accountId, voucherSourceId }) => {
    return axios.delete(`/user/voucher-source/${accountId}/${voucherSourceId}`)
}

// External voucher redemption. Read-only for the provider: validates the code and persists a
// voucher-redemption doc (status "checked"), returning its id + the matched/unmatched tickets. It
// never burns — the burn happens server-side during order finalization.
// Route is scoped by accountId in the PATH (feat/voucher-3-check: /payment/voucher/check/:accountId).
export const voucherCheck = async ({ accountId, eventId, voucherSourceId, code, timeSlot, endUser }) => {
    return axios.post(`/payment/voucher/check/${accountId}`, { eventId, voucherSourceId, code, timeSlot, endUser })
}

export const createCoupon = async ({ accountId, eventId, couponData }) => {
    if (!eventId) {
        return axios.post(`/payment/coupon/create/${accountId}`, couponData)
    }
    return axios.post(`/payment/coupon/create/${accountId}/${eventId}`, couponData)
}

export const updateCoupon = async ({ couponId, accountId, couponData }) => {
    return axios.put(`/payment/coupon/update/${accountId}/${couponId}`, couponData)
}

export const deleteCoupon = async ({ accountId, couponId }) => {
    return axios.delete(`/payment/coupon/delete/${accountId}/${couponId}`)
}

export const applyCouponToAllActiveEvents = async ({ accountId, couponName, couponData }) => {
    return axios.post(`/payment/coupon/applyToAllActiveEvents/${accountId}`, { couponName, couponData })
}

// iCount income types (per-seller managed list). The backend resolves the parent
// seller automatically when given a sub-account id, so `sellerId` here is just the
// current account id (subId).
export const createIncomeType = ({ sellerId, name, incomeTypeId }) => {
    return axios.post(`/payment/income-type/create/${sellerId}`, { name, incomeTypeId })
}

export const getIncomeTypesBySeller = ({ sellerId }) => {
    return axios.get(`/payment/income-types/seller/${sellerId}`)
}

export const getIncomeTypeById = ({ sellerId, incomeTypeId }) => {
    return axios.get(`/payment/income-type/get/${sellerId}/${incomeTypeId}`)
}

export const updateIncomeType = ({ sellerId, incomeTypeId, incomeTypeData }) => {
    return axios.put(`/payment/income-type/update/${sellerId}/${incomeTypeId}`, incomeTypeData)
}

export const deleteIncomeType = ({ sellerId, incomeTypeId }) => {
    return axios.delete(`/payment/income-type/delete/${sellerId}/${incomeTypeId}`)
}

// Legacy
export const generateRefund = async ({ accountId, eventId, refundData }) => {
    return axios.post(`/payment/refund-sale/${accountId}/${eventId}`, refundData)
}

// New
export const refund = async ({ accountId, eventId, refundData }) => {
    return axios.post(`/payment/refund/${accountId}/${eventId}`, refundData)
}

export const getChargesOfEventOrder = async ({ accountId, eventId, eventOrderId }) => {
    return axios.get(`/payment/charge/getByEventOrder/${accountId}/${eventId}/${eventOrderId}`)
}

export const uploadSellerDocs = ({ accountId, filesData }) => {
    return axios.post(`/payment/partner/uploadDocuments/${accountId}`, filesData)
}

export const getOrderPrice = ({ accountId, orderData }) => {
    return axios.post(`/payment/getPrice/${accountId}`, orderData)
}

export const createSeasonTicket = ({ marketplaceId, sellerId, seasonTicketData }) => {
    return axios.post(`/payment/seasonTicket/create/${marketplaceId}/${sellerId}`, seasonTicketData)
}

export const updateSeasonTicket = ({ marketplaceId, sellerId, seasonTicketId, seasonTicketData }) => {
    return axios.put(`/payment/seasonTicket/update/${marketplaceId}/${sellerId}/${seasonTicketId}`, seasonTicketData)
}

export const getSeasonTicketsOfSellerPublic = ({ marketplaceId, sellerId }) => {
    return axios.get(`/payment/seasonTicket/getBySellerIdPublic/${marketplaceId}/${sellerId}`)
}

// `withVisitCounts` adds per-card gate-entry counts (`visitCount`, optional `lastVisitAt`) — nimi-backend#1312.
// The BE checks for the literal string "true"; without it the response is unchanged.
export const getEndUserSeasonTickets = ({ marketplaceId, sellerId, endUserId, withVisitCounts = false }) => {
    return axios.get(`/payment/endUserSeasonTicket/getByEndUserId/${marketplaceId}/${sellerId}/${endUserId}`, {
        params: withVisitCounts ? { withVisitCounts: 'true' } : undefined,
    })
}

export const createEndUserSeasonTicket = ({ marketplaceId, sellerId, endUserId, endUserSeasonTicketData }) => {
    return axios.post(`/payment/endUserSeasonTicket/create/${marketplaceId}/${sellerId}/${endUserId}`, endUserSeasonTicketData)
}

// POS (card-present) season-ticket sale — async: `initiate` returns fast with a pending
// transaction, the terminal charge runs on the BE, and the FE polls the status endpoint.
// Replaces the old synchronous create-with-paymentMethod:"pos" that timed out into a
// "Network Error" (nimi-app#876 / nimi-backend#799).
//
// Finite per-request timeouts: the shared axios instance sets no default timeout, so a hung
// initiate/poll would block runPosSale inside `await` and prevent its 180s poll loop from
// ever reaching timed_out. On a status timeout runPosSale swallows the error and keeps
// polling until the business ceiling, so a single stalled poll can't wedge the flow.
const POS_INITIATE_TIMEOUT_MS = 30000;
const POS_STATUS_TIMEOUT_MS = 20000;

export const initiatePosSeasonTicketSale = ({ marketplaceId, sellerId, endUserId, endUserSeasonTicketData }) => {
    return axios.post(`/payment/endUserSeasonTicket/pos-sale/initiate/${marketplaceId}/${sellerId}/${endUserId}`, endUserSeasonTicketData, { timeout: POS_INITIATE_TIMEOUT_MS })
}

export const getPosSeasonTicketSaleStatus = ({ marketplaceId, sellerId, transactionId }) => {
    return axios.get(`/payment/endUserSeasonTicket/pos-sale/${marketplaceId}/${sellerId}/${transactionId}/status`, { timeout: POS_STATUS_TIMEOUT_MS })
}

export const updateEndUserSeasonTicket = ({ marketplaceId, sellerId, id, endUserSeasonTicketData }) => {
    return axios.put(`/payment/endUserSeasonTicket/update/${marketplaceId}/${sellerId}/${id}`, { endUserSeasonTicket: endUserSeasonTicketData })
}

export const updateEndUserSeasonTicketStatus = ({ marketplaceId, sellerId, id, endUserSeasonTicketData }) => {
    return axios.put(`/payment/endUserSeasonTicket/updateStatus/${marketplaceId}/${sellerId}/${id}`, { status: endUserSeasonTicketData.status })
}

// Records that a membership card was printed (nimi-backend#1513). Resolves to
// { printedAt, printCount } — printCount is incremented server-side, never set, so two
// staff printing at once produce 2 rather than both producing 1.
// Call it AFTER the print was actually issued: a print the user cancels must not inflate
// the count, which exists to surface genuine reprints.
export const markEndUserSeasonTicketPrinted = ({ marketplaceId, sellerId, endUserSeasonTicketId }) => {
    return axios.put(`/payment/endUserSeasonTicket/markPrinted/${marketplaceId}/${sellerId}/${endUserSeasonTicketId}`)
}

export const leanCreateEndUserSeasonTicket = ({ sellerId, endUserId, endUserSeasonTicketData }) => {
    return axios.post(`/payment/endUserSeasonTicket/createLean/${sellerId}/${endUserId}`, endUserSeasonTicketData)
}

export const publicCreateEndUserSeasonTicket = ({ sellerId, endUserSeasonTicketData }) => {
    return axios.post(`/payment/endUserSeasonTicket/publicCreate/${sellerId}`, endUserSeasonTicketData)
}

export const getEndUserSeasonTicketByHrc = async ({ marketplaceId, sellerId, eventId, hrc, timeSlotId }) => {
    // Pass the selected timeSlotId (when purchasing a timeslot event) so the backend scopes the
    // season-ticket usage check per slot instead of per event — otherwise a member who used the
    // subscription on one slot is wrongly blocked on every other slot. Omitted for non-timeslot
    // events / scanner (backend keeps event-level behavior). Pairs with BE #759.
    const query = new URLSearchParams({ hrc: String(hrc) })
    if (timeSlotId) query.set('timeSlotId', String(timeSlotId))
    return axios.get(`/payment/endUserSeasonTicket/getByHrc/${marketplaceId}/${sellerId}/${eventId}?${query.toString()}`)
}

export const getTotalSeasonTicketsEarningBySellerId = async ({ marketplaceId, sellerId, startDate, endDate }) => {
    return axios.get(`/payment/endUserSeasonTicket/totalEarningBySellerId/${marketplaceId}/${sellerId}?startDate=${startDate}&endDate=${endDate}`)
}

export const getFamilyMembers = ({ marketplaceId, sellerId, parentSubscriptionId, token }) =>
    axios.get(`/payment/endUserSeasonTicket/family/${marketplaceId}/${sellerId}/${parentSubscriptionId}`, {
        headers: { jwt: token },
    });

export const completeFamilyProfile = ({ marketplaceId, sellerId, id, token, data }) =>
    axios.put(`/payment/endUserSeasonTicket/completeProfile/${marketplaceId}/${sellerId}/${id}`, data, {
        headers: { jwt: token },
    });

// Family Management — Admin (uses admin JWT, no custom jwt header)
export const adminGetFamilyMembers = ({ marketplaceId, sellerId, parentSubscriptionId }) =>
    axios.get(`/payment/endUserSeasonTicket/family-admin/${marketplaceId}/${sellerId}/${parentSubscriptionId}`)

export const adminDeleteFamilyMember = ({ marketplaceId, sellerId, endUserSeasonTicketId }) =>
    axios.delete(`/payment/endUserSeasonTicket/delete/${marketplaceId}/${sellerId}/${endUserSeasonTicketId}`)

export const adminAddFamilyChild = ({ marketplaceId, sellerId, parentSubscriptionId, data }) =>
    axios.post(`/payment/endUserSeasonTicket/add-child/${marketplaceId}/${sellerId}/${parentSubscriptionId}`, data)

export const adminAddFamilyAdult = ({ marketplaceId, sellerId, parentSubscriptionId, data }) =>
    axios.post(`/payment/endUserSeasonTicket/add-adult/${marketplaceId}/${sellerId}/${parentSubscriptionId}`, data)

/* ------------------ Analytics ------------------ */
export const getUtmsOfEvent = ({ accountId, eventId }) => {
    return axios.get(`/analytics/utm/getUtmsOfEvent/${accountId}/${eventId}`)
}

export const incUtm = ({ accountId, eventId, utmData }) => {
    return axios.put(`/analytics/utm/inc/${accountId}/${eventId}`, utmData)
}

/* ----------------- SMS Campaign ------------------*/


export const sendSms = ({ marketplaceId, smsData, sellerId }) => {
    return axios.post(`/send-notification/sms/send/${marketplaceId}/${sellerId}`, smsData)
}
// /send-notification/sms/smsCampaign/
export const getSmsCampaignByAccountId = ({ accountId }) => {
    return axios.get(`/send-notification/sms/smsCampaign/account/${accountId}`)
}

export const getSmsCampaignBySellerId = ({ sellerId }) => {
    return axios.get(`/send-notification/sms/smsCampaign/seller/${sellerId}`)
}

export const getSmsCampaignByMarketplaceId = ({ marketplaceId }) => {
    return axios.get(`/send-notification/sms/smsCampaign/${marketplaceId}`)
}

/**
 * Upcoming public events across the marketplaces the backend features
 * together (ZOA / Hormesis / Comedy Club). The marketplace list lives on
 * the server on purpose - the client only asks for a limit.
 */
export const getFeaturedPublicEvents = async ({ timezone, limit } = {}) => {
    const params = new URLSearchParams()
    if (timezone) params.set('timezone', timezone)
    if (limit) params.set('limit', String(limit))
    const query = params.toString()
    return axios.get(`/event/public-events/featured${query ? `?${query}` : ''}`)
}

export const getPublicEvents = async ({ type, id, timezone }) => {
    let url = `/event/public-events/${type}/${id}`
    if (timezone) {
        url += `?timezone=${timezone}`
    }
    return axios.get(url)
}

///event/public-subscriptions/:marketplaceId/:sellerId
export const getPublicSubscriptions = async ({ marketplaceId, sellerId }) => {
    return axios.get(`/event/public-subscriptions/${marketplaceId}/${sellerId}`)
}

export const updateMarketplace = async ({ marketplaceId, data }) => {
    return axios.put(`/user/marketplace/update/${marketplaceId}`, data)
}

export const getPermissionToUploadFiles = async ({ contentType, key }) => {
    return axios.get(`/user/getPermissionToUploadFiles?contentType=${contentType}&key=${key}`)
}

export const generateSaleTopUp = async ({ marketplaceId, sellerId, endUserId, buyerKey, amount }) => {
    return axios.post(`/payment/generate-sale-top-up/${marketplaceId}/${sellerId}/${endUserId}`, { buyerKey, amount })
}

export const getEndUserPermissionToUploadFiles = async ({ contentType, key, token }) => {
    return axios.get(`/user/enduser/getPermissionToUploadFiles?contentType=${contentType}&key=${key}`, {
        headers: token ? { jwt: token } : {}
    })
}

// Server-built, deduped, token-free list of the customer's usable cards.
// Each card: { cardId, buyerCardMask, buyerCardExp, buyerName, provider, source, isExpired }.
// cardId is opaque — echo it back to charge/subscribe, never parse it.
export const getSavedCards = async ({ marketplaceId, sellerId, endUserId }) => {
    return axios.get(`/payment/saved-cards/${marketplaceId}/${sellerId}/${endUserId}`)
}

// Admin removes a card from a customer's saved-card list by its opaque cardId. The BE marks
// it removed so it stops appearing and can't be reused; it does NOT stop any live PayMe
// subscription bound to that card (those bill off PayMe's own token) — see the delete alert.
// Unknown cardId → 400: refresh the list and surface the error.
export const deleteSavedCard = async ({ marketplaceId, sellerId, endUserId, cardId }) => {
    return axios.delete(`/payment/saved-cards/${marketplaceId}/${sellerId}/${endUserId}/${cardId}`)
}

// Admin saves/replaces a customer's card on file (addressed by endUserId — the admin twin of
// the customer's own save-card). buyerKey is a freshly tokenized card; the raw token is never
// returned. Sets the customer's card-on-file used for NEW charges (does not touch a live
// subscription's card — PayMe has no update-card). On the USER service.
export const saveEndUserCardByAdmin = async ({ marketplaceId, sellerId, endUserId, buyerKey, buyerCardMask, buyerCardExp, buyerName }) =>
    axios.post(`/user/enduser/saved-card/${marketplaceId}/${sellerId}/${endUserId}`, { buyerKey, buyerCardMask, buyerCardExp, buyerName })

// Un-hides a previously-deleted card (same mask+exp). Idempotent — a no-op when there's no
// tombstone — so always call it after a replace so re-adding a deleted card works.
export const reactivateSavedCard = async ({ marketplaceId, sellerId, endUserId, buyerCardMask, buyerCardExp }) =>
    axios.post(`/payment/saved-cards/${marketplaceId}/${sellerId}/${endUserId}/reactivate`, { buyerCardMask, buyerCardExp })

// cardId → charge an existing saved card (BE resolves the token). buyerKey → a freshly
// tokenized new card. Never both; the BE rejects an unknown cardId with a hard 400.
export const chargeEndUser = async ({ marketplaceId, sellerId, endUserId, amount, description, cardId, buyerKey, storeBuyerKey }) => {
    return axios.post(`/payment/charge-end-user/${marketplaceId}/${sellerId}/${endUserId}`, { amount, description, cardId, buyerKey, storeBuyerKey })
}

export const getEndUserCharges = async ({ marketplaceId, sellerId, endUserId }) => {
    return axios.get(`/payment/charges/endUser/${marketplaceId}/${sellerId}/${endUserId}`)
}

export const getEndUserSeasonTicketTransactions = async ({ marketplaceId, sellerId, endUserSeasonTicketId }) => {
    return axios.get(
        `/payment/enduser-season-ticket/${marketplaceId}/${sellerId}/${endUserSeasonTicketId}/transactions`
    );
};

// Admin (re)generation of a missing invoice for a subscription charge (issue #900 / BE #818).
// Route param order is accountId(=sellerId) then chargeId. Idempotent on the BE: returns the
// existing invoice with alreadyExisted:true if one is already present.
export const generateChargeInvoice = async ({ sellerId, chargeId }) => {
    return axios.post(`/payment/charge/generate-invoice/${sellerId}/${chargeId}`);
};

// Refund a manual (chargeEndUser) charge — dining charges included. `amount` is in SHEKELS (major
// units, matching the charge's own price), strictly positive, at most 2 decimals; partial refunds
// are allowed and repeat calls are capped server-side at what's left. Returns
// { refundChargeId, refundedAmount, totalRefunded, remaining, invoiceNumber?, invoiceUrl? } — the
// invoice pair is the reversing credit note, absent when the seller doesn't self-issue documents or
// the sale's own invoice never landed, so callers must treat it as optional.
export const refundManualCharge = async ({ sellerId, chargeId, amount, reason }) => {
    return axios.post(`/payment/charge/refund/${sellerId}/${chargeId}`, { amount, ...(reason ? { reason } : {}) });
};

export const refundSeasonTicketIteration = async ({ marketplaceId, sellerId, endUserSeasonTicketId, chargeId, amount, reason }) => {
    return axios.post(
        `/payment/enduser-season-ticket/${marketplaceId}/${sellerId}/${endUserSeasonTicketId}/iterations/${chargeId}/refund`,
        { amount, reason }
    );
};

export const refundSeasonTicketOneTime = async ({ marketplaceId, sellerId, endUserSeasonTicketId, chargeId, amount, reason }) => {
    return axios.post(
        `/payment/enduser-season-ticket/${marketplaceId}/${sellerId}/${endUserSeasonTicketId}/one-time/${chargeId}/refund`,
        { amount, reason }
    );
};

// Async POS-capable one-time refund — mirrors the async POS sale (initiate returns fast, then
// poll the status endpoint while the BE drives the terminal). The synchronous
// refundSeasonTicketOneTime above risks gateway 504s / duplicate refunds on card-present
// refunds. `initiate` resolves inline with status:"completed" for online (non-POS) refunds;
// only status:"pending" is polled. Reuses the POS-sale per-request timeouts. #890 / BE #805.
export const initiatePosSeasonTicketOneTimeRefund = ({ marketplaceId, sellerId, endUserSeasonTicketId, chargeId, amount, reason, language, idempotencyKey }) => {
    return axios.post(
        `/payment/enduser-season-ticket/${marketplaceId}/${sellerId}/${endUserSeasonTicketId}/one-time/${chargeId}/refund/initiate`,
        { amount, reason, language, idempotencyKey },
        { timeout: POS_INITIATE_TIMEOUT_MS }
    );
};

export const getPosSeasonTicketOneTimeRefundStatus = ({ marketplaceId, sellerId, transactionId }) => {
    return axios.get(
        `/payment/enduser-season-ticket/${marketplaceId}/${sellerId}/one-time/refund/${transactionId}/status`,
        { timeout: POS_STATUS_TIMEOUT_MS }
    );
};

export const sendVerificationCode = async ({ marketplaceId, phone, sellerId, nonLocalPhone, shouldCreateUserIfNotExist, webOtp }) => {
    // webOtp=true asks the backend to append the origin-bound "@host #code" WebOTP line to the SMS
    // (opt-in; existing callers omit it so their SMS is byte-identical to before).
    return axios.post(`/user/enduser/sendVerificationCode/${marketplaceId}`, { phone }, { params: { sellerId, nonLocalPhone, shouldCreateUserIfNotExist, webOtp } })
}

export const verifyCode = async ({ marketplaceId, phone, code, seller }) => {
    return axios.post(`/user/enduser/verifyCode/${marketplaceId}`, { phone, verificationCode: code, seller })
}

export const sendVerificationCodeSimple = async ({ phone, marketplaceId, sellerId }) => {
    // query params shouldCreateUserIfNotExist false
    return axios.post(`/user/enduser/simplePhoneVerification/${marketplaceId}/${sellerId}`, { phone })
}
export const verifyCodeSimple = async ({ phone, code }) => {
    return axios.post(`/user/enduser/simpleVerifyCode`, { phone, verificationCode: code })
}

export const getEndUserDataByTokenAndSellerHandler = async ({ sellerHandler, token }) => {
    return axios.get(`/user/enduser/getDataByTokenAndSeller/${sellerHandler}`, { headers: { [AUTH_HEADER_KEY]: token } })
}

// A dining member's own meal history, for the buyer-facing dining page.
//
// ⚠️ There is deliberately NO endUserId parameter, and one must never be added. The id is taken
// from the buyer's token server-side; only the seller id is in the URL, and that is public. An
// endUserId here would let any signed-in guest read another guest's balance and meals by editing
// the address bar — which is exactly why the admin dining routes, which DO take one in the path,
// are admin-only.
//
// The token is passed explicitly rather than relying on the axios default header: that default
// carries the DASHBOARD jwt, which a buyer does not have.
//
// 401 means the buyer token is missing, invalid or expired — treat it as logged-out, not an error.
//
// 200: { commits: [ { _id, visitDate, siteName, amount, orderTotal, settled } ],
//        pagination: { total, page, limit, totalPages } }
export const getMyDiningCommits = async ({ sellerId, token, page = 1, limit = 20 }) => {
    return axios.get(`/crm/dining/my-commits/${sellerId}`, {
        headers: { [AUTH_HEADER_KEY]: token },
        params: { page, limit },
    })
}

export const setSelectedHrc = async ({ sellerHandler, selectedHrc, token }) => {
    return axios.post(`/user/enduser/setSelectedHrc/${sellerHandler}`, { selectedHrc }, { headers: { [AUTH_HEADER_KEY]: token } })
}

export const updateEndUserSelf = async ({ endUserData, token }) => {
    return axios.put(`/user/enduser/updateByToken`, endUserData, { headers: { [AUTH_HEADER_KEY]: token } })
}

export const saveEndUserCard = async ({ cardData, token }) =>
    axios.post(`/user/enduser/saved-card`, cardData, { headers: { [AUTH_HEADER_KEY]: token } })

export const deleteEndUserCard = async ({ token }) =>
    axios.delete(`/user/enduser/saved-card`, { headers: { [AUTH_HEADER_KEY]: token } })


export const registerEndUser = async ({ marketplaceId, sellerId, endUserData }) => {
    return axios.post(`/user/enduser/register/${marketplaceId}/${sellerId}`, endUserData)
}

export const getSellerByIdPublic = async ({ sellerId }) => {
    return axios.get(`/user/seller/public/${sellerId}`)
}

const requireField = (field, name) => {
    if (!field) {
        throw new Error(`${name} is required`)
    }
}

export const createDivideOrder = async ({ data }) => {
    const { marketplace, seller, _id: eventOrderId, event: eventId } = data
    requireField(marketplace, "marketplace")
    requireField(seller, "seller")
    requireField(data, "data")
    return axios.post(`/payment/divide-order/createSplitProduct/${marketplace}/${seller}`, { eventOrderId, eventId, accountId: seller })
}


// New calendar event API methods using hard-coded routes
export const getCalendarEvents = async (marketplaceId, sellerId, startDate, endDate) => {
    let query = `?startDate=${startDate}`;
    if (endDate) query += `&endDate=${endDate}`;
    const url = `/crm/calendar-event/${marketplaceId}/${sellerId}${query}`;
    return axios.get(url);
};

export const createCalendarEvent = async (marketplaceId, sellerId, eventData) => {
    const url = `/crm/calendar-event/${marketplaceId}/${sellerId}`;
    return axios.post(url, eventData);
};

export const updateCalendarEvent = async (calendarEventId, marketplaceId, sellerId, eventData) => {
    const url = `/crm/calendar-event/${calendarEventId}/${marketplaceId}/${sellerId}`;
    return axios.put(url, eventData);
};

export const deleteCalendarEvent = async (calendarEventId, marketplaceId, sellerId) => {
    const url = `/crm/calendar-event/${calendarEventId}/${marketplaceId}/${sellerId}`;
    return axios.delete(url);
};

export const getMyMembers = async ({ marketplaceId, sellerId }) => {
    return axios.get(`/payment/endUserSeasonTicket/my-members/${marketplaceId}/${sellerId}`)
}

export const createVisit = async ({ marketplaceId, sellerId, endUserId, visitData }) => {
    return axios.post(`/crm/visit/${marketplaceId}/${sellerId}/${endUserId}`, visitData)
}
export const getVisitById = async ({ visitId, marketplaceId, sellerId }) => {
    return axios.get(`/crm/visit/${visitId}/${marketplaceId}/${sellerId}`)
}
export const incrementGuests = async ({ marketplaceId, sellerId, endUserId, body }) => {
    return axios.put(`/crm/visit/increment/${marketplaceId}/${sellerId}/${endUserId}`, body)
}
export const decrementGuests = async ({ marketplaceId, sellerId, endUserId, body }) => {
    return axios.put(`/crm/visit/decrement/${marketplaceId}/${sellerId}/${endUserId}`, body)
}
export const exitVisit = async ({ marketplaceId, sellerId, endUserId }) => {
    return axios.put(`/crm/visit/exit/${marketplaceId}/${sellerId}/${endUserId}`)
}
export const getActiveVisits = async ({ marketplaceId, sellerId }) => {
    return axios.get(`/crm/visit/active/${marketplaceId}/${sellerId}`)
}
export const getActiveVisitsByEndUser = async ({ endUserId }) => {
    return axios.get(`/crm/visit/active/enduser/${endUserId}`)
}


export const createEndUserComment = async ({ marketplaceId, sellerId, commentData }) => {
    return axios.post(`/crm/end-user-comment/${marketplaceId}/${sellerId}`, commentData)
}
export const getEndUserCommentById = async ({ commentId, marketplaceId, sellerId }) => {
    return axios.get(`/crm/end-user-comment/${commentId}/${marketplaceId}/${sellerId}`)
}
export const updateEndUserComment = async ({ commentId, marketplaceId, sellerId, commentData }) => {
    return axios.put(`/crm/end-user-comment/${commentId}/${marketplaceId}/${sellerId}`, commentData)
}
export const deleteEndUserComment = async ({ commentId, marketplaceId, sellerId }) => {
    return axios.delete(`/crm/end-user-comment/${commentId}/${marketplaceId}/${sellerId}`)
}
//  getByEndUser: "/crm/end-user-comment/end-user/:endUserId/:marketplaceId/:sellerId",
export const getEndUserCommentsByEndUser = async ({ endUserId, marketplaceId, sellerId }) => {
    return axios.get(`/crm/end-user-comment/end-user/${endUserId}/${marketplaceId}/${sellerId}`)
}
// "/crm/visit/update/:marketplaceId/:sellerId/:endUserId"
export const updateVisit = async ({ marketplaceId, sellerId, endUserId, visitData }) => {
    return axios.put(`/crm/visit/update/${marketplaceId}/${sellerId}/${endUserId}`, visitData)
}

// Get visit history with filters
export const getVisitHistory = async ({ marketplaceId, sellerId, startDate, endDate, endUserId, seasonTicketId, scannerSessionId, page = 1, limit = 50 }) => {
    const params = { startDate, endDate, page, limit };
    if (endUserId) {
        params.endUserId = endUserId;
    }
    if (seasonTicketId) {
        params.seasonTicketId = seasonTicketId;
    }
    if (scannerSessionId) {
        params.scannerSessionId = scannerSessionId;
    }

    return axios.get(`/crm/visit/history/${marketplaceId}/${sellerId}`, { params });
}

// Dining credit: one member's grant — the toggle state and monthly amount. Filtered by endUserId so
// it returns a single record, and unlike the balance endpoint it still returns members whose credit
// is switched off (their stored amount must stay visible so the form can show it).
export const getEndUserDiningMember = async ({ marketplaceId, sellerId, endUserId }) => {
    return axios.get(`/crm/dining/member/${marketplaceId}/${sellerId}`, { params: { endUserId } });
}

// Dining credit: remaining balance for a member. Returns 403 not_a_dining_member when they are not
// enrolled or their credit is switched off — a state to render, not an error to report.
export const getEndUserDiningBalance = async ({ marketplaceId, sellerId, endUserId }) => {
    return axios.get(`/crm/dining/balance/${marketplaceId}/${sellerId}/${endUserId}`);
}

// Dining credit: enrol a member, change their monthly amount, or toggle the credit. Both body fields
// are optional — sending only isActive leaves the stored amount untouched.
export const upsertEndUserDiningMember = async ({ marketplaceId, sellerId, endUserId, monthlyAmount, isActive }) => {
    const body = {};
    if (monthlyAmount !== undefined) body.monthlyAmount = monthlyAmount;
    if (isActive !== undefined) body.isActive = isActive;

    return axios.put(`/crm/dining/member/${marketplaceId}/${sellerId}/${endUserId}`, body);
}

// Dining credit: the aggregated totals for the Overview cards + donut, over the WHOLE date
// window for this member (never a page). Split from the rows endpoint by nimi-backend#1085 —
// see getEndUserDiningCommits below. Unlike the rows endpoint, this one does NOT take
// search/settled/refundState: it's a period summary ("what happened in this window"), not a
// total of whatever the History list happens to be filtered to. Do not add those params here —
// the BE ignores them today, but wiring them up would make the call look like it filters when
// it doesn't, inviting someone to "fix" a bug that doesn't exist.
export const getEndUserDiningTotals = async ({ marketplaceId, sellerId, endUserId, startDate, endDate }) => {
    const params = {};
    if (endUserId) params.endUserId = endUserId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    return axios.get(`/crm/dining/commits/totals/${marketplaceId}/${sellerId}`, { params });
}

// Dining credit: a member's meals for a date range, paged. Since nimi-backend#1085 this endpoint
// only returns rows + pagination — see getEndUserDiningTotals above for the summary. includeTotals
// is hardcoded false: nothing here ever needs the two aggregates the server can skip on a page
// fetch, since the Overview reads them from the totals endpoint instead.
export const getEndUserDiningCommits = async ({ marketplaceId, sellerId, endUserId, startDate, endDate, search, settled, refundState, page = 1, limit = 50 }) => {
    const params = { page, limit, includeTotals: false };
    if (endUserId) params.endUserId = endUserId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (search) params.search = search;
    if (settled !== undefined) params.settled = settled;
    if (refundState) params.refundState = refundState;

    return axios.get(`/crm/dining/commits/${marketplaceId}/${sellerId}`, { params });
}

// Dining credit: a member's meals not yet billed — the source list for "build a charge" and the
// only place Compensate is offered (a settled meal is rejected with commit_already_settled).
export const getEndUserUnbilledDiningCommits = async ({ marketplaceId, sellerId, endUserId }) => {
    return axios.get(`/crm/dining/commits/unbilled/${marketplaceId}/${sellerId}/${endUserId}`);
}

// Dining credit: the server-authoritative total for a set of unbilled meals. commitIds must be a
// comma-separated string of 24-hex ids (no spaces, no trailing comma) — the schema regex rejects
// anything else with a plain 400 before it reaches domain logic. Always call this right before
// charging; a compensation can land on a meal between when the checklist was loaded and now, so a
// client-side sum can go stale.
export const previewSelectedDiningCommits = async ({ marketplaceId, sellerId, endUserId, commitIds }) => {
    return axios.get(`/crm/dining/commits/preview-selected/${marketplaceId}/${sellerId}/${endUserId}`, {
        params: { commitIds: Array.isArray(commitIds) ? commitIds.join(',') : commitIds },
    });
}

// Dining credit: mark a selected set of unbilled meals as billed by the charge just created via
// chargeEndUser. All-or-nothing — if any commitId is stale (already billed elsewhere) the whole
// request is rejected and NOTHING is settled, even though the charge itself already succeeded.
// Dining credit: mark the selected meals as billed against a charge. Goes through the PAYMENT
// service rather than CRM's settle-selected: CRM cannot see the charges collection, so it accepted
// any well-formed chargeId — which is how a batch of meals ended up settled against an unrelated,
// already-refunded charge. This route verifies the charge belongs to this member+seller, succeeded,
// and is neither a refund row nor fully refunded, before settling in CRM. CRM's own
// invalid_commit_selection still passes through unchanged (all-or-nothing, nothing is settled).
export const settleSelectedDiningCommits = async ({ marketplaceId, sellerId, endUserId, commitIds, chargeId }) => {
    return axios.post(`/payment/charge/${sellerId}/${chargeId}/settle-dining`, {
        marketplaceId,
        endUserId,
        commitIds,
    });
}

// Dining credit: per-charge billing history (sale + its refunds), in the same row shape as the
// season-ticket transactions endpoint so the same dialog renders both.
export const getChargeTransactions = async ({ sellerId, chargeId }) => {
    return axios.get(`/payment/charge/${sellerId}/${chargeId}/transactions`);
}

// Dining credit: apply a pre-charge price adjustment to one unbilled meal. amount must be strictly
// negative and cannot push the meal's effectiveAmount below 0 (compensation_exceeds_price).
export const compensateDiningCommit = async ({ marketplaceId, sellerId, commitId, amount, reason }) => {
    return axios.post(`/crm/dining/commits/compensate/${marketplaceId}/${sellerId}/${commitId}`, {
        amount,
        reason,
    });
}

// Dining credit: past charges for a member, grouped by chargeId with the meals each one collected.
export const getEndUserDiningCharges = async ({ marketplaceId, sellerId, endUserId, page = 1, limit = 50 }) => {
    return axios.get(`/crm/dining/charges/${marketplaceId}/${sellerId}/${endUserId}`, {
        params: { page, limit },
    });
}

export const getMembershipStatisticsByTimePeriod = async ({ marketplaceId, sellerId, dateField }) => {
    return axios.get(`/payment/endUserSeasonTicket/membership-statistics-time-period/${marketplaceId}/${sellerId}?dateField=${dateField}`)
}

export const getEventOrdersByUTM = async ({ accountId, eventId, utm }) => {
    return axios.get(`/user-event/event-order/getByUTM/${accountId}/${eventId}/${utm}`);
}

export const getEventLinkByUtm = async ({ utm }) => {
    return axios.get(`/event/event-link/getByUtm/${utm}`);
}

/* ------------------ Time Slots ------------------ */
export const getEventScheduleConfig = async (eventId) => {
    // Check for mock data first
    const { getMockScheduleConfig } = await import('../services/mockService');
    const mockResponse = await getMockScheduleConfig(eventId);
    if (mockResponse) return mockResponse;

    return axios.get(`/event/schedule/${eventId}`)
}

export const getTimeSlotAvailability = async ({ eventId, date }) => {
    // Check for mock data first
    const { getMockTimeSlotAvailability } = await import('../services/mockService');
    const mockResponse = await getMockTimeSlotAvailability(eventId, date);
    if (mockResponse) return mockResponse;

    return axios.get(`/event/${eventId}/availability/${date}`)
}

export const bookTimeSlot = async ({ accountId, eventId, timeSlotData }) => {
    return axios.post(`/event/${accountId}/${eventId}/book-slot`, timeSlotData)
}

export const getEventBookingsForDate = async ({ accountId, eventId, date }) => {
    return axios.get(`/event/${accountId}/${eventId}/bookings/${date}`)
}

export const updateTimeSlotConfig = async ({ accountId, eventId, timeSlotConfig }) => {
    // Send timeSlotConfig fields directly in the request body (not wrapped)
    return axios.put(`/event/time-slot-config/${accountId}/${eventId}`, timeSlotConfig)
}

export const getTimeSlotConfig = async ({ accountId, eventId }) => {
    return axios.get(`/event/time-slot-config/${accountId}/${eventId}`)
}

export const approveSeller = async ({ marketplaceId, sellerId }) => {
    return axios.put(`/user/approve-seller/${marketplaceId}/${sellerId}`);
}

// My Sellers housekeeping (#1354 / BE #1198): reject a PENDING seller, archive an APPROVED one, or
// restore either. Marketplace-owner guarded. Resolves with the updated account (rejectedAt /
// archivedAt set or null). 409 = the seller is no longer in the state the action needs.
export const setSellerReviewState = ({ marketplaceId, sellerId, action }) => {
    return axios.post(`/user/marketplace/${marketplaceId}/sellers/${sellerId}/${action}`)
}

export const getMyLeads = async ({ marketplaceId, sellerId }) => {
    // "/user/enduser/my-leads/:sellerId",
    return axios.get(`/user/enduser/my-leads/${sellerId}`)
}

// =====================================================================
// STRIPE CONNECT API ENDPOINTS
// =====================================================================

/**
 * Check Stripe account status for a seller (requires JWT)
 */
export const getStripeAccountStatus = async (accountId) => {
    return axios.get(`/payment/partner/stripe/connect/status/${accountId}`);
}

/**
 * Get public Stripe initialization data for a seller (no JWT required)
 */
export const getPublicStripeInit = async (sellerId) => {
    return axios.get(`/payment/public/stripe/init/${sellerId}`);
}

/**
 * Create or get Stripe Connect onboarding link
 * Note: email, country, businessType are optional to enable Stripe's Networked Onboarding
 * which allows users to connect existing Stripe accounts
 */
export const createStripeConnectLink = async (accountId, { returnUrl, refreshUrl, email, country, businessType } = {}) => {
    const payload = {
        accountId,
        returnUrl,
        refreshUrl,
    };
    // Only include optional fields if provided - omitting them enables Networked Onboarding
    if (email) payload.email = email;
    if (country) payload.country = country;
    if (businessType) payload.businessType = businessType;

    return axios.post('/payment/partner/stripe/connect/initiate', payload);
}

/**
 * Get Stripe Express Dashboard login link
 */
export const getStripeDashboardLink = async (accountId) => {
    return axios.get(`/payment/partner/stripe/connect/dashboard-link/${accountId}`);
}

/**
 * Create a Payment Intent for Stripe
 */
export const createStripePaymentIntent = async ({
    amount,
    currency,
    externalAccountId,
    eventId,
    description,
    metadata,
    customerId,
    applicationFeeAmount
}) => {
    return axios.post('/payment/partner/stripe/create-payment-intent', {
        amount,
        currency,
        externalAccountId,
        eventId,
        description,
        metadata,
        customerId,
        applicationFeeAmount
    });
}

/**
 * Create a Stripe Setup Intent for event purchases (NEW FLOW)
 * This captures the payment method WITHOUT charging the customer
 * Backend will charge later when ready (manual approval or immediate)
 */
export const createStripeSetupIntent = async ({
    eventId,
    sellerId,
    endUserId,
    email,
    name,
    phone
}) => {
    return axios.post('/payment/event/stripe/create-setup-intent', {
        eventId,
        sellerId,
        endUserId,
        email,
        name,
        phone
    });
}

/**
 * Confirm a Stripe payment
 */
export const confirmStripePayment = async (paymentIntentId) => {
    return axios.post(`/payment/partner/stripe/confirm-payment/${paymentIntentId}`);
}

/**
 * Create a Stripe refund
 */
export const createStripeRefund = async ({
    paymentIntentId,
    amount,
    reason,
    metadata
}) => {
    return axios.post('/payment/partner/stripe/create-refund', {
        paymentIntentId,
        amount,
        reason,
        metadata
    });
}

/**
 * Create or get a Stripe customer
 */
export const createStripeCustomer = async ({
    email,
    name,
    phone,
    accountId,
    metadata
}) => {
    return axios.post('/payment/partner/stripe/create-customer', {
        email,
        name,
        phone,
        accountId,
        metadata
    });
}

/**
 * Create a Setup Intent for subscription/season ticket purchases
 * Note: Different endpoint and parameters than createStripeSetupIntent (event flow)
 */
export const createStripeSetupIntentForSubscription = async ({
    customerId,
    accountId,
    metadata
}) => {
    return axios.post('/payment/partner/stripe/create-setup-intent', {
        customerId,
        accountId,
        metadata
    });
}

/**
 * List payment methods for a customer
 */
export const getStripePaymentMethods = async (customerId) => {
    return axios.get(`/payment/partner/stripe/payment-methods/${customerId}`);
}

/**
 * Detach a payment method from a customer
 */
export const detachStripePaymentMethod = async (paymentMethodId) => {
    return axios.post(`/payment/partner/stripe/detach-payment-method/${paymentMethodId}`);
}

// ========================== End User Import ==========================

/**
 * Preview import file - returns columns and sample data
 */
export const previewImportFile = ({ marketplaceId, accountId, fileUrl }) => {
    return axios.post(`/user/enduser-import/preview/${marketplaceId}/${accountId}`, { fileUrl });
}

/**
 * Initiate end user import from Excel/CSV file
 */
export const initiateEndUserImport = ({ marketplaceId, accountId, fileUrl, segmentName, columnMapping, defaultCountryCode }) => {
    return axios.post(`/user/enduser-import/initiate/${marketplaceId}/${accountId}`, {
        fileUrl,
        segmentName,
        columnMapping,
        defaultCountryCode
    });
}

/**
 * Get import job status
 */
export const getImportJobStatus = ({ marketplaceId, accountId, jobId }) => {
    return axios.get(`/user/enduser-import/status/${marketplaceId}/${accountId}/${jobId}`);
}

/**
 * Get valid end user fields for column mapping
 */
export const getValidEndUserFields = ({ marketplaceId, accountId }) => {
    return axios.get(`/user/enduser-import/fields/${marketplaceId}/${accountId}`);
}

/**
 * Cancel an in-progress end user import job
 */
export const cancelImportJob = ({ marketplaceId, accountId, jobId }) => {
    return axios.put(`/user/enduser-import/cancel/${marketplaceId}/${accountId}/${jobId}`);
}

/* ---- Basket / POS ---- */

export const basketGetPrice = ({ accountId, data }) => {
    return axios.post(`/payment/basket/get-price/${accountId}`, data);
}

export const basketPurchase = ({ accountId, sellerId, purchaseData }) => {
    return axios.post(`/payment/basket/purchase/${accountId}/${sellerId}`, purchaseData);
}

// Async POS basket sale (nimi-app#1086 / nimi-backend#940): a card-present terminal charge
// outlives the gateway timeout, so POS baskets go initiate → background charge → status poll
// instead of the synchronous purchase above. Same finite per-request timeouts as the
// season-ticket pos-sale pair — the shared axios instance has no default timeout, and a hung
// request would wedge runPosSale inside `await` before its business ceiling could fire.
// purchaseData must carry the client-generated idempotencyKey (retries reuse it; BE dedupes).
export const initiateBasketPosSale = ({ accountId, sellerId, purchaseData }) => {
    return axios.post(`/payment/basket/pos-sale/initiate/${accountId}/${sellerId}`, purchaseData, { timeout: POS_INITIATE_TIMEOUT_MS });
}

export const getBasketPosSaleStatus = ({ accountId, sellerId, transactionId }) => {
    return axios.get(`/payment/basket/pos-sale/${accountId}/${sellerId}/${transactionId}/status`, { timeout: POS_STATUS_TIMEOUT_MS });
}

export const basketRefund = async ({ accountId, basketOrderId, refundData }) => {
    return axios.post(`/payment/basket/refund/${accountId}/${basketOrderId}`, refundData);
}

export const basketUpdate = async ({ accountId, basketOrderId, updateData }) => {
    return axios.post(`/payment/basket/update/${accountId}/${basketOrderId}`, updateData);
}

export const createPosDevice = ({ marketplaceId, sellerId, data }) => {
    return axios.post(`/payment/pos-device/create/${marketplaceId}/${sellerId}`, data);
}

export const getPosDevicesBySeller = ({ marketplaceId, sellerId }) => {
    return axios.get(`/payment/pos-device/getBySellerId/${marketplaceId}/${sellerId}`);
}

export const updatePosDevice = ({ marketplaceId, sellerId, deviceId, data }) => {
    return axios.put(`/payment/pos-device/update/${marketplaceId}/${sellerId}/${deviceId}`, data);
}

export const deletePosDevice = ({ marketplaceId, sellerId, deviceId }) => {
    return axios.delete(`/payment/pos-device/delete/${marketplaceId}/${sellerId}/${deviceId}`);
}

// AdyTech devices only (400 pos_device_not_adytech for Payme devices).
// Returns { status, online } with HTTP 200 even when the terminal is unreachable.
export const getPosDeviceStatus = ({ marketplaceId, sellerId, deviceId }) => {
    return axios.get(`/payment/pos-device/status/${marketplaceId}/${sellerId}/${deviceId}`);
}

export const resetPosDevice = ({ marketplaceId, sellerId, deviceId }) => {
    return axios.post(`/payment/pos-device/reset/${marketplaceId}/${sellerId}/${deviceId}`);
}

export const getBasketOrderById = ({ accountId, basketOrderId }) => {
    return axios.get(`/user-event/basket-order/getById/${accountId}/${basketOrderId}`);
}

export const getBasketOrderPublic = (basketOrderId) => {
    return axios.get(`/user-event/basket-order/public/${basketOrderId}`);
}

export const getBasketOrderByNumber = ({ accountId, basketOrderNumber }) => {
    return axios.get(`/user-event/basket-order/getByNumber/${accountId}/${basketOrderNumber}`);
}

export const getBasketOrdersBySeller = ({ accountId, page = 1, limit = 20, search = '', status = '' }) => {
    const params = `page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}${status ? `&status=${status}` : ''}`;
    return axios.get(`/user-event/basket-order/getBySeller/${accountId}?${params}`);
}

export const getBasketOrderAnalytics = ({ accountId, params }) => {
    const searchParams = new URLSearchParams();
    if (params.from) searchParams.set('from', params.from);
    if (params.to) searchParams.set('to', params.to);
    if (params.type) searchParams.set('type', params.type);
    if (params.timezone) searchParams.set('timezone', params.timezone);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.createdByIds?.length) {
        params.createdByIds.forEach((id) => searchParams.append('createdByIds', id));
    }
    return axios.get(`/user-event/basket-order/analytics/${accountId}?${searchParams.toString()}`);
}

export const getBasketOrderSummary = ({ accountId, params }) => {
    const searchParams = new URLSearchParams();
    if (params.from) searchParams.set('from', params.from);
    if (params.to) searchParams.set('to', params.to);
    if (params.type) searchParams.set('type', params.type);
    if (params.createdByIds?.length) {
        params.createdByIds.forEach((id) => searchParams.append('createdByIds', id));
    }
    return axios.get(`/user-event/basket-order/summary/${accountId}?${searchParams.toString()}`);
}

// Counter subscription sales never create a basket order, so the reports drawer adds this to
// getBasketOrderSummary (same response shape). createdByIds filters by the selling user (soldBy, nimi-backend#1388).
export const getSeasonTicketSalesSummary = ({ accountId, params }) => {
    const searchParams = new URLSearchParams();
    if (params.from) searchParams.set('from', params.from);
    if (params.to) searchParams.set('to', params.to);
    if (params.type) searchParams.set('type', params.type);
    if (params.createdByIds?.length) {
        params.createdByIds.forEach((id) => searchParams.append('createdByIds', id));
    }
    return axios.get(`/payment/season-ticket-sales/summary/${accountId}?${searchParams.toString()}`);
}

/* ------------------ Z reports (cashier register summaries) ------------------ */
// from/to are "YYYY-MM-DD" strings; amounts in responses are shekels.
export const getZReports = ({ accountId, from, to }) => {
    const params = new URLSearchParams({ from, to });
    return axios.get(`/payment/z-report/list/${accountId}?${params.toString()}`);
}

export const getXReport = ({ accountId }) => {
    return axios.get(`/payment/z-report/x/${accountId}`);
}

export const getZRangeReport = ({ accountId, from, to }) => {
    const params = new URLSearchParams({ from, to });
    return axios.get(`/payment/z-report/range/${accountId}?${params.toString()}`);
}

export const getChargesByBasketOrder = (basketOrderId, { status } = {}) => {
    // Repeated keys (?status=a&status=b), not a comma-joined value — the backend's default
    // querystring parser only arrays-ifies repeated keys, so a single joined string would
    // silently match nothing.
    const params = new URLSearchParams();
    (Array.isArray(status) ? status : status ? [status] : []).forEach((s) => params.append('status', s));
    const qs = params.toString();
    return axios.get(`/payment/charge/getByBasketOrder/${basketOrderId}${qs ? `?${qs}` : ''}`);
}

export const getBasketQrCheckInList = ({ accountId, basketOrderId }) => {
    return axios.get(`/user-event/basket-qr-check-in/${accountId}/${basketOrderId}`);
}

export const basketQrCheckIn = ({ accountId, basketOrderId, eventId }) => {
    return axios.put(`/user-event/basket-qr-check-in/${accountId}/${basketOrderId}/${eventId}`);
}

export const basketQrCheckInByTickets = ({ accountId, basketOrderId, tickets }) => {
    return axios.put(`/user-event/basket-qr-check-in/${accountId}/${basketOrderId}`, { tickets });
}

// Group-order QR check-in (nimi-app#1292 item 5 / nimi-backend#1124 item 5) — mirrors the
// basket-QR pair above exactly: GET the group's held captures/tickets grouped by event, PUT to
// check in every approved ticket not yet checked in for one event.
// The scanner session is the credential here (gate tablets never log in), so sessionId is
// required in the path — the BE checks it exists, belongs to accountId, and covers the event.
export const getGroupQrCheckInList = ({ accountId, sessionId, groupOrderId }) => {
    return axios.get(`/user-event/group-qr-check-in/${accountId}/${sessionId}/${groupOrderId}`);
}

export const groupQrCheckIn = ({ accountId, sessionId, groupOrderId, eventId, timeSlotId }) => {
    // No body — this route takes none (mirrors basketQrCheckIn above). Passing `null` here made
    // axios fall back to its default PUT content-type (application/x-www-form-urlencoded, since
    // `null` isn't recognized as a plain object to JSON-encode), which the BE has no parser for
    // and 415s on. `undefined` skips the body/content-type entirely.
    return axios.put(`/user-event/group-qr-check-in/${accountId}/${sessionId}/${groupOrderId}/${eventId}`, undefined, {
        params: timeSlotId ? { timeSlotId } : undefined,
    });
}

// Scanner Sessions CRUD
export const createScannerSession = ({ accountId, data }) => {
    return axios.post(`/user-event/scanner-session/create/${accountId}`, data);
}

export const getScannerSessionsByAccount = ({ accountId }) => {
    return axios.get(`/user-event/scanner-session/list/${accountId}`);
}

export const getScannerSessionPublic = ({ sessionId }) => {
    return axios.get(`/user-event/scanner-session/public/${sessionId}`);
}

export const updateScannerSession = ({ accountId, sessionId, data }) => {
    return axios.put(`/user-event/scanner-session/update/${accountId}/${sessionId}`, data);
}

export const deleteScannerSession = ({ accountId, sessionId }) => {
    return axios.delete(`/user-event/scanner-session/delete/${accountId}/${sessionId}`);
}

// Session check-in (resolves eventId from userEventId on backend).
// With sessionId the BE refuses an out-of-scope scan BEFORE writing (403 event_not_in_scanner_session)
// and records the station on the ticket. Without sessionId, no body is sent and the BE behaves as before.
export const sessionCheckIn = ({ accountId, userEventId, ticketInfoId, sessionId }) =>
    axios.put(
        `/user-event/session-qr-check-in/${accountId}/${userEventId}/${ticketInfoId}`,
        sessionId ? { sessionId } : undefined,
    );

// Session subscription check-in (single member)
export const sessionSubscriptionCheckIn = ({ sellerId, sessionId, endUserSeasonTicketId, autoExit }) =>
    axios.put(`/payment/session-subscription-check-in/${sellerId}/${sessionId}/${endUserSeasonTicketId}`, { autoExit });

// Session subscription batch check-in (multiple family members)
export const sessionSubscriptionBatchCheckIn = ({ sellerId, sessionId, endUserSeasonTicketIds, autoExit }) =>
    axios.put(`/payment/session-subscription-check-in-batch/${sellerId}/${sessionId}`, { endUserSeasonTicketIds, autoExit });

export const getTimeSlotSummary = ({ accountId, eventId, date }) =>
    axios.get(`/user-event/event-order/getTimeSlotSummary/${accountId}/${eventId}`, {
        params: { date },
    }).then(res => res.data);

export const getEventOrdersByDate = ({ accountId, eventId, date, timeSlotId, status, search, page, limit, freeTickets, signal }) =>
    axios.get(`/user-event/event-order/getEventOrdersByDate/${accountId}/${eventId}`, {
        params: {
            date,
            timeSlotId,
            status: status && status !== 'all' ? status : undefined,
            search: search || undefined,
            page,
            limit,
            // When true the BE returns the event's FREE_TICKET orders, ignoring date/timeSlotId
            freeTickets: freeTickets || undefined,
        },
        signal,
    }).then(res => res.data);

/* ------------------ Nimi Invoices (self-issued, BE invoices service) ------------------ */

export const getNimiInvoiceSettings = ({ sellerId }) => {
    return axios.get(`/invoices/settings/get/${sellerId}`)
}

export const saveNimiInvoiceSettings = ({ sellerId, settings }) => {
    return axios.post(`/invoices/settings/${sellerId}`, settings)
}

export const getOpenFormatSummary = ({ sellerId, startDate, endDate }) => {
    return axios.post(`/invoices/open-format/summary/${sellerId}`, { start_date: startDate, end_date: endDate })
}

export const exportOpenFormat = ({ sellerId, startDate, endDate }) => {
    return axios.post(
        `/invoices/open-format/export/${sellerId}`,
        { start_date: startDate, end_date: endDate },
        { responseType: 'blob' }
    )
}

export const exportRivhit = ({ sellerId, startDate, endDate }) => {
    return axios.post(
        `/invoices/rivhit/export/${sellerId}`,
        { start_date: startDate, end_date: endDate }
    )
}

// Group orders (CRM service) — extracted into its own module; re-exported so consumers keep
// using `import * as API from '../api'`.
export * from './groupOrders.js';
export * from './whatsapp.js';
